import { Transaction, VersionedTransaction, PublicKey } from "@solana/web3.js";
import { type ConnectedStandardSolanaWallet } from "@privy-io/react-auth/solana";
import { isVersionedTransaction } from "@bridgesplit/react";
import { base58 } from "@bridgesplit/utils";

import { LoopscaleWallet } from "./types";

function isVersionedTransactionHelper(tx: Transaction | VersionedTransaction): tx is VersionedTransaction {
    return tx instanceof VersionedTransaction || isVersionedTransaction(tx);
}

/**
 * Serializes a Solana transaction to Uint8Array
 */
function serializeTransaction(transaction: Transaction | VersionedTransaction): Uint8Array {
    if (isVersionedTransactionHelper(transaction)) {
        return transaction.serialize();
    }
    const buffer = transaction.serialize({ requireAllSignatures: false, verifySignatures: false });
    // Always wrap in Uint8Array to ensure consistent typing (Buffer extends Uint8Array but has incompatible type signature)
    return new Uint8Array(buffer);
}

/**
 * Deserializes a Uint8Array back to a Solana transaction
 */
function deserializeTransaction<T extends Transaction | VersionedTransaction>(transaction: T, bytes: Uint8Array): T {
    if (isVersionedTransactionHelper(transaction)) {
        return VersionedTransaction.deserialize(bytes) as T;
    }
    return Transaction.from(bytes) as T;
}

/**
 * Converts Privy's response format (single or array) to a single response
 */
function toSingleResponse<T>(response: T | T[]): T {
    return Array.isArray(response) ? response[0] : response;
}

/**
 * Manually adds a 64-byte signature to a transaction
 */
function addSignatureToTransaction<T extends Transaction | VersionedTransaction>(
    transaction: T,
    serialized: Uint8Array,
    signature: Uint8Array,
    publicKey: PublicKey
): T {
    if (isVersionedTransactionHelper(transaction)) {
        const clonedTx = VersionedTransaction.deserialize(serialized);
        clonedTx.addSignature(publicKey, signature);
        return clonedTx as T;
    } else {
        const clonedTx = Transaction.from(serialized);
        clonedTx.addSignature(publicKey, Buffer.from(signature));
        return clonedTx as T;
    }
}

function signatureToBase58(sig: string | Uint8Array): string {
    if (typeof sig === "string") {
        return sig.includes(",") ? base58.encode(new Uint8Array(sig.split(",").map(Number))) : sig;
    }
    return base58.encode(sig);
}

/**
 * Convert a Privy ConnectedStandardSolanaWallet to a LoopscaleWallet
 */
export function buildLoopscaleWalletFromPrivy(
    wallet: ConnectedStandardSolanaWallet,
    walletClientType: string | undefined
): LoopscaleWallet {
    const publicKey = new PublicKey(wallet.address);

    const signTransaction = async <T extends Transaction | VersionedTransaction>(transaction: T): Promise<T> => {
        const serialized = serializeTransaction(transaction);
        const signedResponse = await wallet.signTransaction({ transaction: serialized });
        const signedTransaction = toSingleResponse(signedResponse).signedTransaction;

        // Some wallet connect integrations do not follow expected full transaction responses
        // Privy release special fix - 3.9.0-beta-20251212212932
        if (signedTransaction.length === 64) {
            return addSignatureToTransaction(transaction, serialized, signedTransaction, publicKey);
        }

        // Normal path: full signed transaction returned
        return deserializeTransaction(transaction, signedTransaction);
    };

    const signAllTransactions = async <T extends Transaction | VersionedTransaction>(
        transactions: T[]
    ): Promise<T[]> => {
        if (transactions.length === 0) return [];
        if (transactions.length === 1) return [await signTransaction(transactions[0])];

        const serializedTxs = transactions.map((tx) => serializeTransaction(tx));
        const signedResponses = await wallet.signTransaction(...serializedTxs.map((transaction) => ({ transaction })));

        return transactions.map((originalTx, index) => {
            const signedTx = signedResponses[index].signedTransaction;
            return deserializeTransaction(originalTx, signedTx);
        });
    };

    const signMessage = wallet.signMessage
        ? async (message: Uint8Array) => {
              const response = await wallet.signMessage({ message });
              return toSingleResponse(response).signature;
          }
        : undefined;

    const signAndSendTransactions = async <T extends Transaction | VersionedTransaction>(
        transactions: T[]
    ): Promise<{ signature: string }[]> => {
        if (transactions.length === 0) return [];
        const inputs = transactions.map((tx) => ({
            transaction: serializeTransaction(tx),
            chain: "solana:mainnet" as const
        }));
        const responses = await wallet.signAndSendAllTransactions(inputs);
        return responses.map((r) => ({
            signature: signatureToBase58(r.signature)
        }));
    };

    return {
        publicKey,
        walletAddress: wallet.address,
        signTransaction,
        signAllTransactions,
        signAndSendTransactions,
        signMessage,
        walletClientType
    };
}

export function normalizePrivyWalletClientType(clientType: string | undefined): string | undefined {
    return typeof clientType === "string" ? clientType?.trim().toLowerCase() : undefined;
}

export function getPrivyWalletMatch(
    privyWallets: ConnectedStandardSolanaWallet[],
    stateWalletAddress: string | undefined,
    stateClientType: string | undefined
) {
    if (!stateWalletAddress || !stateClientType || !privyWallets?.length) {
        return undefined;
    }

    return privyWallets.find((wallet) => {
        // try just to be safe with to lowercase
        const privyWalletAddress = wallet?.address;
        const privyWalletClientType = normalizePrivyWalletClientType(wallet?.standardWallet?.name);
        try {
            return privyWalletAddress === stateWalletAddress && privyWalletClientType === stateClientType;
        } catch {
            return false;
        }
    });
}
